import { useState, useEffect, useCallback, useMemo } from 'react';
import { usePersistedState } from './use-persisted-state';
import { apiClient } from '@/lib/api-client';
import { calculateAgeInMonths } from '@/lib/actions';
import type { SleepEvent, Child, SleepGoal } from '@/lib/types';
import { getAICoachTemporalContext, getTimeOfDay } from '@/lib/coach-context';

interface Message {
  role: 'user' | 'model';
  content: string;
  timestamp: Date;
}

interface SuggestedPromptsState {
  prompts: string[];
  lastUpdated: number; // Store as timestamp
  conversationHash: string;
}

export function getDefaultPromptsForAge(months: number, childName: string): string[] {
  if (months <= 3) {
    return [
      `What are normal newborn wake windows for ${childName}?`,
      `How do I tell if ${childName} is overtired vs hungry?`,
      `Tips for distinguishing day vs night sleep?`,
    ];
  } else if (months <= 6) {
    return [
      `Signs of the 4-month sleep regression in ${childName}?`,
      `How to establish a reliable bedtime routine?`,
      `Are ${childName}'s wake windows on track for 3 naps?`,
    ];
  } else if (months <= 9) {
    return [
      `Is ${childName} ready to transition to 2 naps?`,
      `How can I handle early morning wake-ups?`,
      `What should bedtime look like at this age?`,
    ];
  } else if (months <= 14) {
    return [
      `How to transition ${childName} to 1 nap?`,
      `Why is ${childName} suddenly fighting naps?`,
      `Tips for extending night sleep to 11+ hours?`,
    ];
  } else if (months <= 24) {
    return [
      `What is the ideal wake window before bedtime for ${childName}?`,
      `How to handle toddler bedtime resistance?`,
      `Is 1 nap enough for ${childName} right now?`,
    ];
  } else {
    return [
      `How much total sleep does ${childName} need at 2+ years?`,
      `Handling bedtime stalling and night fears?`,
      `When should we transition from crib to toddler bed?`,
    ];
  }
}

export function useSuggestedPrompts(
  events: SleepEvent[],
  child: Child | null | undefined,
  messages: Message[],
  goal?: SleepGoal
) {
  const [suggestedPrompts, setSuggestedPrompts] = usePersistedState<SuggestedPromptsState>(
    'ai-coach-suggested-prompts',
    {
      prompts: [],
      lastUpdated: 0,
      conversationHash: ''
    }
  );

  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);

  // Generate a hash of the conversation to detect meaningful changes
  const generateConversationHash = useCallback((msgs: Message[]) => {
    if (msgs.length === 0) return '';

    // Only consider user messages for context changes (AI responses don't change the context)
    const userMessages = msgs.filter(msg => msg.role === 'user');

    // Take the last 2 user messages to detect meaningful context changes
    // This is more conservative and only triggers on actual user input changes
    const recentUserMessages = userMessages.slice(-2);
    const content = recentUserMessages.map(msg => msg.content).join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  }, []);

  // Check if we should update the prompts
  const shouldUpdatePrompts = useCallback(() => {
    if (!child) return false;

    const currentHash = generateConversationHash(messages);
    const isFirstTime = messages.length <= 1; // Only welcome message

    // More conservative update conditions:
    // 1. No prompts exist at all
    // 2. It's the first time and we don't have prompts
    // 3. Conversation hash has changed AND we have at least 2 user messages (meaningful conversation)
    // 4. It's been more than 24 hours since last update AND we have some conversation
    // 5. Minimum 5 minutes between generations to prevent spam
    const hasChanged = currentHash !== suggestedPrompts.conversationHash;
    const hasMeaningfulConversation = messages.filter(msg => msg.role === 'user').length >= 2;
    const isStale = Date.now() - suggestedPrompts.lastUpdated > 24 * 60 * 60 * 1000;
    const needsInitialPrompts = isFirstTime && suggestedPrompts.prompts.length === 0;
    const noPromptsExist = suggestedPrompts.prompts.length === 0;
    const minTimeElapsed = Date.now() - suggestedPrompts.lastUpdated > 5 * 60 * 1000; // 5 minutes

    // Only update if:
    // - No prompts exist at all, OR
    // - It's first time with no prompts, OR  
    // - Hash changed AND we have meaningful conversation AND minimum time elapsed, OR
    // - Stale AND we have some conversation
    const shouldUpdate = noPromptsExist || needsInitialPrompts || (hasChanged && hasMeaningfulConversation && minTimeElapsed) || (isStale && hasMeaningfulConversation);

    return shouldUpdate;
  }, [child, messages, suggestedPrompts, generateConversationHash]);

  // Generate new suggested prompts
  const generatePrompts = useCallback(async () => {
    if (!child) return;

    setIsLoadingPrompts(true);

    try {
      // Build real-time temporal context
      const now = new Date();
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const temporalContext = getAICoachTemporalContext(events, now, goal, timezone);
      const timeOfDay = getTimeOfDay(now, timezone);

      // Add sleep goals context if available
      const sleepGoalsContext = goal ? `
Sleep Goals:
- Bedtime: ${goal.bedtime}
- Wake-up time: ${goal.wakeUpTime}
- Total night sleep: ${goal.totalNightSleep} hours
- Number of naps: ${goal.numNaps}
- Maximum awake window: ${goal.maxAwakeWindow} hours
- Total 24h sleep: ${goal.totalSleep24h} hours
` : '';

      const childInfo = `${child.name} (${child.gender}, ${calculateAge(child.birthMonth, child.birthYear)} months old)`;
      const isFirstTime = messages.length <= 1;

      // Limit conversation history to last 15 messages for suggested prompts
      const limitedMessages = messages.slice(-15);

      const response = await apiClient.generateSuggestedPrompts({
        childInfo,
        sleepData: temporalContext.formattedSleepData + (sleepGoalsContext ? '\n\n' + sleepGoalsContext : ''),
        conversationHistory: limitedMessages.map(msg => ({
          role: msg.role,
          content: [{ text: msg.content }]
        })),
        isFirstTime,
        currentTime: temporalContext.currentTime,
        timezone: temporalContext.timezone,
        currentTimeFormatted: temporalContext.currentTimeFormatted,
        timeOfDay,
        currentStatusSummary: temporalContext.currentStatusSummary
      });

      if (!response.success) {
        // Throw user-friendly error message (don't expose server error details)
        throw new Error('Unable to generate suggested prompts. Please try again.');
      }

      if (response.data) {
        const currentHash = generateConversationHash(messages);
        setSuggestedPrompts({
          prompts: response.data.prompts,
          lastUpdated: Date.now(),
          conversationHash: currentHash
        });
      }
    } catch (error) {
      console.error('Error generating suggested prompts:', error);
      // Re-throw the error so the calling component can handle it with toast/UI feedback
      throw error;
    } finally {
      setIsLoadingPrompts(false);
    }
  }, [child, events, messages, goal, shouldUpdatePrompts, generateConversationHash, setSuggestedPrompts]);

  // Calculate child's age in months
  const calculateAge = (birthMonth: number, birthYear: number) => {
    const today = new Date();
    const birthDate = new Date(birthYear, birthMonth - 1);
    let ageYears = today.getFullYear() - birthDate.getFullYear();
    let ageMonths = today.getMonth() - birthDate.getMonth();

    if (ageMonths < 0 || (ageMonths === 0 && today.getDate() < birthDate.getDate())) {
      ageYears--;
      ageMonths += 12;
    }

    return ageYears * 12 + ageMonths;
  };

  // Auto-generate prompts when needed - DISABLED for manual generation
  // useEffect(() => {
  //   if (shouldUpdatePrompts()) {
  //     generatePrompts();
  //   }
  // }, [shouldUpdatePrompts, generatePrompts]);

  // Clear prompts when child changes
  useEffect(() => {
    if (child) {
      setSuggestedPrompts({
        prompts: [],
        lastUpdated: 0,
        conversationHash: ''
      });
    }
  }, [child?.id, setSuggestedPrompts]);

  // Clear current prompts
  const clearPrompts = useCallback(() => {
    setSuggestedPrompts(prev => ({
      ...prev,
      prompts: []
    }));
  }, [setSuggestedPrompts]);

  const defaultPrompts = useMemo(() => {
    if (!child) return [];
    const ageMonths = calculateAgeInMonths(child.birthMonth, child.birthYear);
    return getDefaultPromptsForAge(ageMonths, child.name);
  }, [child]);

  const activePrompts = suggestedPrompts.prompts.length > 0
    ? suggestedPrompts.prompts
    : defaultPrompts;

  return {
    suggestedPrompts: activePrompts,
    isLoadingPrompts,
    generatePrompts,
    clearPrompts
  };
}
